import React from 'react';
import { Link } from 'react-router-dom';
import {
  CommandLineIcon,
  CubeTransparentIcon,
  PhotoIcon,
  RocketLaunchIcon,
  WrenchScrewdriverIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';

const guides = [
  {
    title: 'Create Token',
    desc: 'Launch a VSC-7 fungible token with metadata, reputation scoring, and optional presale.',
    href: '/guide/tokens',
    icon: CubeTransparentIcon,
    color: 'text-vextaris-blue',
  },
  {
    title: 'Intent Studio (VIPs)',
    desc: 'Build, register, simulate, and invoke Vextaris Intent Programs — declarative on-chain logic.',
    href: '/guide/vips',
    icon: CommandLineIcon,
    color: 'text-vextaris-purple',
  },
  {
    title: 'Deploy Suite',
    desc: 'Use templates, the terminal, or the JSON editor to rapidly deploy tokens, NFTs, pools, and presales.',
    href: '/guide/deploy',
    icon: WrenchScrewdriverIcon,
    color: 'text-vextaris-lime',
  },
  {
    title: 'NFT Collections',
    desc: 'Create VSC-21 NFT collections with royalties, metadata lock, and on-chain provenance.',
    href: '/guide/nfts',
    icon: PhotoIcon,
    color: 'text-pink-400',
  },
  {
    title: 'Presales',
    desc: 'Launch anti-rug presales with escrow, soft/hard caps, LP auto-lock, and refund guarantees.',
    href: '/guide/presales',
    icon: RocketLaunchIcon,
    color: 'text-orange-400',
  },
];

export default function Guide() {
  return (
    <div className="max-w-4xl mx-auto space-y-16">

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-4">VexForge Guide</h1>
        <p className="text-gray-400 text-lg leading-relaxed">
          VexForge is the developer toolchain for the Vextaris blockchain.
          It provides a web interface for creating tokens, deploying programs, launching presales,
          and managing on-chain assets — all without writing or compiling smart contracts.
        </p>
      </div>

      {/* What makes VexForge different */}
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-white">How VexForge works</h2>
        <div className="space-y-4 text-gray-400 leading-relaxed">
          <p>
            Unlike Ethereum or Solana where you write bytecode and deploy contracts,
            Vextaris has <span className="text-white">104+ native operations</span> built directly into the protocol.
            Token creation, NFT minting, AMM swaps, multi-sig custody, governance — these are
            protocol-level primitives, not smart contracts.
          </p>
          <p>
            VexForge gives you a UI for every operation the chain supports. Fill a form, connect your
            wallet, submit. The chain handles the rest. For more complex logic, <span className="text-white">VIPs
            (Vextaris Intent Programs)</span> let you compose multiple operations into reusable,
            parameterized programs — still no bytecode, still no compiler.
          </p>
          <p>
            Every token gets an on-chain <span className="text-white">reputation score</span> based on
            contract mechanics (mint authority, liquidity lock, creator history, metadata integrity).
            VIPs are <span className="text-white">statically analyzed</span> at registration time for
            dangerous patterns. Safety features, not safety promises — always DYOR.
          </p>
        </div>
      </div>

      {/* Safety callout */}
      <div className="flex items-start space-x-4 bg-vextaris-darker border border-gray-800 rounded-xl p-6">
        <ShieldCheckIcon className="w-6 h-6 text-vextaris-blue flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-white font-medium mb-1">Safety by design</h3>
          <p className="text-gray-400 text-sm leading-relaxed">
            VexForge and the Vextaris protocol implement safety features at multiple layers:
            token reputation scoring, VIP static analysis, presale escrow, metadata sanitization,
            and community flagging. These reduce risk but do not eliminate it.
            Always review what you are signing. Always DYOR.
          </p>
        </div>
      </div>

      {/* Tool guides */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-white">Tool guides</h2>
        <div className="space-y-3">
          {guides.map((g) => {
            const Icon = g.icon;
            return (
              <Link
                key={g.title}
                to={g.href}
                className="flex items-center justify-between bg-vextaris-darker border border-gray-800 rounded-xl p-5 hover:border-gray-700 transition-colors group"
              >
                <div className="flex items-center space-x-4">
                  <Icon className={`w-6 h-6 ${g.color} flex-shrink-0`} />
                  <div>
                    <h3 className="text-white font-medium group-hover:text-vextaris-blue transition-colors">{g.title}</h3>
                    <p className="text-gray-500 text-sm mt-0.5">{g.desc}</p>
                  </div>
                </div>
                <ArrowRightIcon className="w-4 h-4 text-gray-600 group-hover:text-white transition-colors flex-shrink-0" />
              </Link>
            );
          })}
        </div>
      </div>

      {/* External resources */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-white">Resources</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <a href="https://docs.vextaris.io" target="_blank" rel="noopener noreferrer"
            className="flex items-center space-x-3 bg-vextaris-darker border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors">
            <BookOpenIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-300 text-sm">Full Documentation</span>
          </a>
          <a href="https://docs.vextaris.io/developers/rpc" target="_blank" rel="noopener noreferrer"
            className="flex items-center space-x-3 bg-vextaris-darker border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors">
            <CommandLineIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-300 text-sm">RPC Reference</span>
          </a>
          <a href="https://vexscan.io" target="_blank" rel="noopener noreferrer"
            className="flex items-center space-x-3 bg-vextaris-darker border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-colors">
            <CubeTransparentIcon className="w-5 h-5 text-gray-500" />
            <span className="text-gray-300 text-sm">Block Explorer</span>
          </a>
        </div>
      </div>
    </div>
  );
}
